import { createAsyncThunk, createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { ProjectApi } from "~/functions/api/project";
import type { Project } from "~/interface/project";
import type { RootState } from "~/store";
import { type AuthState, setCurrentProject } from "./auth";

interface CostEffectivenessState {
  preImplementationMonthlyCostYen: number;
  postImplementationMonthlyCostYen: number;
  personPriceMonthPriceYen: number;
  preImplementationMonthlyCostText: string;
  postImplementationMonthlyCostText: string;
  personPriceMonthPriceText: string;
  initialCostYen: number;
  isEditing: boolean;
  isPreImplementationMonthlyCostInvalid: boolean;
  isPostImplementationMonthlyCostInvalid: boolean;
  isPricePerMonthInvalid: boolean;
  error: string | null;
}

const initialState: CostEffectivenessState = {
  preImplementationMonthlyCostYen: 0,
  postImplementationMonthlyCostYen: 0,
  personPriceMonthPriceYen: 0,
  preImplementationMonthlyCostText: "",
  postImplementationMonthlyCostText: "",
  personPriceMonthPriceText: "",
  initialCostYen: 0,
  isEditing: false,
  isPreImplementationMonthlyCostInvalid: false,
  isPostImplementationMonthlyCostInvalid: false,
  isPricePerMonthInvalid: false,
  error: null,
};

const validateInputNumber = (value: string) => value === "" || value.match(/^\d+$/) !== null;

export const updateMonthlyCosts = createAsyncThunk<any, void, { state: RootState }>(
  "costEffectiveness/updateMonthlyCosts",
  async (_, { getState, dispatch, rejectWithValue }) => {
    try {
      const state = getState();
      const { currentProject, organization } = state.auth as AuthState;
      const {
        preImplementationMonthlyCostYen,
        postImplementationMonthlyCostYen,
        personPriceMonthPriceYen,
      } = state.costEffectiveness;

      const projectApi = new ProjectApi();
      const response = await projectApi.updateMonthlyCost(organization.id, currentProject.id, {
        beforeMonthlyCost: preImplementationMonthlyCostYen,
        afterMonthlyCost: postImplementationMonthlyCostYen,
        personPriceMonthPrice: personPriceMonthPriceYen,
      });

      if (response === null) {
        return rejectWithValue("設定の更新に失敗しました。");
      }

      dispatch(
        setCurrentProject({
          ...currentProject,
          costEffectivenessData: {
            beforeMonthlyCost: preImplementationMonthlyCostYen,
            afterMonthlyCost: postImplementationMonthlyCostYen,
            personPriceMonthPrice: personPriceMonthPriceYen,
          },
        }),
      );

      return response;
    } catch (_error) {
      return rejectWithValue("設定の更新に失敗しました。");
    }
  },
);

export const initializeCostEffectiveness = createAsyncThunk(
  "costEffectiveness/initializeCostEffectiveness",
  async (_, { getState }) => {
    const { currentProject } = (getState() as RootState).auth as AuthState;
    return {
      beforeMonthlyCost: currentProject.costEffectivenessData?.beforeMonthlyCost,
      afterMonthlyCost: currentProject.costEffectivenessData?.afterMonthlyCost,
      personPriceMonthPrice: currentProject.costEffectivenessData?.personPriceMonthPrice,
      total_work_loads: currentProject.estimationData?.total_work_loads,
    };
  },
);

export const updateTextsFromCostEffectivenessData = createAsyncThunk(
  "costEffectiveness/updateTextsFromCostEffectivenessData",
  async (_, { getState }) => {
    const { currentProject } = (getState() as RootState).auth as AuthState;
    return {
      beforeMonthlyCost: currentProject.costEffectivenessData?.beforeMonthlyCost,
      afterMonthlyCost: currentProject.costEffectivenessData?.afterMonthlyCost,
      personPriceMonthPrice: currentProject.costEffectivenessData?.personPriceMonthPrice,
    };
  },
);

// セレクタの定義
export const selectCurrentProject = (state: RootState) => (state.auth as AuthState).currentProject;

const costEffectivenessSlice = createSlice({
  name: "costEffectiveness",
  initialState,
  reducers: {
    setPreImplementationMonthlyCost: (state, action: PayloadAction<string>) => {
      state.preImplementationMonthlyCostText = action.payload;
      if (validateInputNumber(action.payload)) {
        state.isPreImplementationMonthlyCostInvalid = false;
        state.preImplementationMonthlyCostYen =
          action.payload === "" ? 0 : parseInt(action.payload, 10);
      } else {
        state.isPreImplementationMonthlyCostInvalid = true;
      }
    },
    setPostImplementationMonthlyCost: (state, action: PayloadAction<string>) => {
      state.postImplementationMonthlyCostText = action.payload;
      if (validateInputNumber(action.payload)) {
        state.isPostImplementationMonthlyCostInvalid = false;
        state.postImplementationMonthlyCostYen =
          action.payload === "" ? 0 : parseInt(action.payload, 10);
      } else {
        state.isPostImplementationMonthlyCostInvalid = true;
      }
    },
    setPersonPriceMonthPrice: (
      state,
      action: PayloadAction<{ value: string; total_work_loads?: number }>,
    ) => {
      state.personPriceMonthPriceText = action.payload.value;
      if (validateInputNumber(action.payload.value)) {
        state.isPricePerMonthInvalid = false;
        const value = action.payload.value === "" ? 0 : parseInt(action.payload.value, 10);
        state.personPriceMonthPriceYen = value;
        if (action.payload.total_work_loads !== undefined) {
          state.initialCostYen = action.payload.total_work_loads * value;
        }
      } else {
        state.isPricePerMonthInvalid = true;
      }
    },
    setInitialCostYen: (state, action: PayloadAction<number>) => {
      // 工数 × 人月単価で初期費用を計算
      state.initialCostYen = action.payload * state.personPriceMonthPriceYen;
    },
    toggleEditMode: (state) => {
      state.isEditing = !state.isEditing;
    },
    cancelEdit: (state, action: PayloadAction<{ currentProject: Project }>) => {
      const { currentProject } = action.payload;
      state.preImplementationMonthlyCostYen =
        currentProject.costEffectivenessData?.beforeMonthlyCost ?? 0;
      state.postImplementationMonthlyCostYen =
        currentProject.costEffectivenessData?.afterMonthlyCost ?? 0;
      state.personPriceMonthPriceYen =
        currentProject.costEffectivenessData?.personPriceMonthPrice ?? 0;
      state.preImplementationMonthlyCostText = currentProject.costEffectivenessData
        ?.beforeMonthlyCost
        ? currentProject.costEffectivenessData.beforeMonthlyCost.toString()
        : "";
      state.postImplementationMonthlyCostText = currentProject.costEffectivenessData
        ?.afterMonthlyCost
        ? currentProject.costEffectivenessData.afterMonthlyCost.toString()
        : "";
      state.personPriceMonthPriceText = currentProject.costEffectivenessData?.personPriceMonthPrice
        ? currentProject.costEffectivenessData.personPriceMonthPrice.toString()
        : "";
      if (currentProject.estimationData?.total_work_loads) {
        state.initialCostYen =
          currentProject.estimationData.total_work_loads * state.personPriceMonthPriceYen;
      }
      state.isPreImplementationMonthlyCostInvalid = false;
      state.isPostImplementationMonthlyCostInvalid = false;
      state.isPricePerMonthInvalid = false;
      state.isEditing = false;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(updateMonthlyCosts.fulfilled, (state) => {
        state.error = null;
      })
      .addCase(updateMonthlyCosts.rejected, (state, action) => {
        state.error = action.payload as string;
      })
      .addCase(initializeCostEffectiveness.fulfilled, (state, action) => {
        const { beforeMonthlyCost, afterMonthlyCost, personPriceMonthPrice, total_work_loads } =
          action.payload;

        state.preImplementationMonthlyCostYen = beforeMonthlyCost ?? 0;
        state.postImplementationMonthlyCostYen = afterMonthlyCost ?? 0;
        state.personPriceMonthPriceYen = personPriceMonthPrice ?? 0;

        if (total_work_loads) {
          state.initialCostYen = total_work_loads * (personPriceMonthPrice ?? 0);
        }

        if (!state.isEditing) {
          state.preImplementationMonthlyCostText = beforeMonthlyCost
            ? beforeMonthlyCost.toString()
            : "";
          state.postImplementationMonthlyCostText = afterMonthlyCost
            ? afterMonthlyCost.toString()
            : "";
          state.personPriceMonthPriceText = personPriceMonthPrice
            ? personPriceMonthPrice.toString()
            : "";
        }
      })
      .addCase(updateTextsFromCostEffectivenessData.fulfilled, (state, action) => {
        const { beforeMonthlyCost, afterMonthlyCost, personPriceMonthPrice } = action.payload;
        if (!state.isEditing) {
          state.preImplementationMonthlyCostText = beforeMonthlyCost
            ? beforeMonthlyCost.toString()
            : "";
          state.postImplementationMonthlyCostText = afterMonthlyCost
            ? afterMonthlyCost.toString()
            : "";
          state.personPriceMonthPriceText = personPriceMonthPrice
            ? personPriceMonthPrice.toString()
            : "";
        }
      });
  },
});

export const {
  setPreImplementationMonthlyCost,
  setPostImplementationMonthlyCost,
  setPersonPriceMonthPrice,
  setInitialCostYen,
  toggleEditMode,
  cancelEdit,
  clearError,
} = costEffectivenessSlice.actions;

export default costEffectivenessSlice.reducer;
